import React, { useEffect, useState } from 'react'
import Tooltip from '@mui/material/Tooltip'
import { t } from '../../i18n'
import consumer from '../../../src/cable'
import { ControlStatusPayload, StatusIndicator, StatusSection } from '../controls/types'
import StatusIndicatorBadge, { classFor, levelModifier } from './StatusIndicatorBadge'
import { LocalizationContextType } from './contexts'
import { formatTenantDateTime } from '../../utils/formatTenantDateTime'

const scope = 'controls'
const STORAGE_KEY = 'controlStatusCollapsed'

const Indicator = ({ indicator }: { indicator: StatusIndicator }) => (
  <StatusIndicatorBadge label={indicator.label} level={indicator.level} detail={indicator.detail} />
)

const TempZone = ({ children }: { children: React.ReactNode }) => (
  <div className="control-status-bar__temp-zone">
    <span className="control-status-bar__temp-badge">
      <i className="bi bi-thermometer-half" aria-hidden="true" />
      {t('control_status.temp_ok', { scope })}
    </span>
    {children}
  </div>
)

const DoorIndicators = ({ section }: { section: StatusSection }) => {
  const doorIndicator = (index: number) => {
    const indicator = section.indicators.find(i => i.key === `door_${index}`)
    return indicator ? <Indicator key={indicator.key} indicator={indicator} /> : null
  }

  // TODO: Re-enable the temp zones once sensors are added.
  return (
    <>
      {doorIndicator(0)}
      {/* <TempZone>{[1, 2, 3].map(doorIndicator)}</TempZone>
      <TempZone>{[4, 5].map(doorIndicator)}</TempZone> */}
      {[1, 2, 3].map(doorIndicator)}
      {[4, 5].map(doorIndicator)}
      {doorIndicator(6)}
    </>
  )
}

const IndicatorRow = ({ section }: { section: StatusSection }) => (
  <div className="control-status-bar__indicators">
    {section.key === 'doors'
      ? <DoorIndicators section={section} />
      : section.indicators.map(indicator => <Indicator key={indicator.key} indicator={indicator} />)}
  </div>
)

const SectionColumn = (
  { section, collapsed, headerExtra }: { section: StatusSection, collapsed: boolean, headerExtra?: React.ReactNode }
) => (
  <div className="control-status-bar__section">
    <div className="control-status-bar__section-header">
      {collapsed && <span className={classFor('control-status-bar__dot', levelModifier(section.level))} />}
      <span className="control-status-bar__label">{section.label}</span>
      {headerExtra}
    </div>
    {!collapsed && <IndicatorRow section={section} />}
  </div>
)

// Every robot-wide state comes from the same RobotStatus snapshot, so they all report the
// same read time.
const lastUpdateTooltip = (lastUpdatedAt: string | null, localization?: LocalizationContextType): string => (
  lastUpdatedAt
    ? t('control_status.last_update_tooltip', { scope, time: formatTenantDateTime(lastUpdatedAt, localization) })
    : t('control_status.never_updated', { scope })
)

// Rides in the Doors header, whose row of doors is far wider than the label — so it reads as
// one robot-wide state (label · dot · state) without claiming a column of its own.
const DoorSafetyIndicator = (
  { indicator, lastUpdatedAt, localization }: {
    indicator: StatusIndicator, lastUpdatedAt: string | null, localization?: LocalizationContextType
  }
) => {
  const dotModifier = indicator.level === 'ok' ? 'armed' : levelModifier(indicator.level)

  return (
    <Tooltip title={lastUpdateTooltip(lastUpdatedAt, localization)} placement="top" arrow>
      <span className="control-status-bar__connection control-status-bar__door-safety">
        <span className="control-status-bar__connection-label">{t('control_status.door_safety', { scope })}</span>
        <span className={classFor('control-status-bar__dot', dotModifier)} />
        <span>{indicator.label}</span>
      </span>
    </Tooltip>
  )
}

const ConnectedIndicator = ({ connected, lastUpdatedAt, localization }: {
  connected: boolean, lastUpdatedAt: string | null, localization?: LocalizationContextType
}) => (
  <Tooltip title={lastUpdateTooltip(lastUpdatedAt, localization)} placement="top" arrow>
    <span className="control-status-bar__connection">
      <span className="control-status-bar__connection-label">{t('control_status.status', { scope })}</span>
      <span className={classFor('control-status-bar__dot', connected ? 'connected' : 'disconnected')} />
      <span>{connected ? t('control_status.connected', { scope }) : t('control_status.disconnected', { scope })}</span>
    </span>
  </Tooltip>
)

const ToggleButton = ({ collapsed, onToggle }: { collapsed: boolean, onToggle: (next: boolean) => void }) => (
  <button type="button" className="control-status-bar__toggle" onClick={() => onToggle(!collapsed)}>
    <i className={`bi bi-chevron-${collapsed ? 'down' : 'up'}`} aria-hidden="true" />
  </button>
)

const ControlStatusBar = ({ locationId, status, localization_context }: {
  locationId: number, status: ControlStatusPayload | null, localization_context?: LocalizationContextType
}) => {
  const [payload, setPayload] = useState<ControlStatusPayload | null>(status ?? null)
  const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem(STORAGE_KEY) === 'true')

  useEffect(() => {
    if (!locationId) return

    const channel = consumer.subscriptions.create(
      { channel: 'ControlStatusChannel', location_id: locationId },
      { received: (data: ControlStatusPayload) => setPayload(data) }
    )

    return () => channel.unsubscribe()
  }, [locationId])

  const toggle = (next: boolean) => {
    setCollapsed(next)
    localStorage.setItem(STORAGE_KEY, String(next))
  }

  if (!payload) return null

  // Expanded, it rides in the Doors header; collapsed there is no room there, so it falls back
  // beside the connection state. Only ever rendered once.
  const doorSafety = (
    <DoorSafetyIndicator
      indicator={payload.door_safety}
      lastUpdatedAt={payload.last_updated_at}
      localization={localization_context}
    />
  )

  return (
    <div className="control-status-bar">
      <div className="control-status-bar__panel">
        <div className="control-status-bar__body">
          {payload.sections.map(section => (
            <SectionColumn
              key={section.key}
              section={section}
              collapsed={collapsed}
              headerExtra={!collapsed && section.key === 'doors' ? doorSafety : undefined}
            />
          ))}
          <div className="control-status-bar__status-group">
            {collapsed && doorSafety}
            <ConnectedIndicator
              connected={payload.connected}
              lastUpdatedAt={payload.last_updated_at}
              localization={localization_context}
            />
            <ToggleButton collapsed={collapsed} onToggle={toggle} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ControlStatusBar
